import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../assets/css/GuesthouseBooking.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const API_BASE = API_ROOT.endsWith("/api") ? API_ROOT : `${API_ROOT}/api`;
const GH_API = `${API_BASE}/guesthouse`;

function formatDateInput(value) {
  if (!value) return "";
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

export default function GuesthouseBookingEditRequest() {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const user = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = parseInt(user?.moduleId, 10);
  const userId = parseInt(user?.id, 10);
  const deptId = parseInt(user?.deptid, 10);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [guestHouses, setGuestHouses] = useState([]);

  const [formData, setFormData] = useState({
    VisitorName: "",
    VisitorMobile: "",
    VisitorEMail: "",
    Department: "",
    Address: "",
    StateID: "",
    CityID: "",
    RelationWithApplicant: "",
    GuestHouseID: "",
    RoomType: "",
    VisitType: "",
    NoOfRooms: "",
    PurposeOfVisit: "",
    FromDate: "",
    ToDate: "",
    NoOfDays: "",
    RecieptNo: "",
    ReceiptDate: "",
    PaymentMode: "",
    Amount: "",
    NAmount: "",
    UPIID: "",
    Remarks: "",
    BookingID: bookingId,
    Status: "",
    SupportDoc: null,
    ICardOccupant: null,
    visitors: [{ name: "", mobile: "", relationship: "Self" }],
    cancelBooking: false
  });

  useEffect(() => {
    document.title = "Change Booking Request (By Emp/Department)";
  }, []);

  useEffect(() => {
    if (![1, 13, 15, 17, 21].includes(moduleId)) {
      setError("Unauthorized access Or Session Timed out.");
      return;
    }

    const fetchLists = async () => {
      try {
        const [stateRes, ghRes] = await Promise.all([
          fetch(`${API_BASE}/states`),
          fetch(`${GH_API}/options?moduleId=10`)
        ]);
        const stateData = await stateRes.json();
        const ghData = await ghRes.json();
        setStates(Array.isArray(stateData) ? stateData : []);
        setGuestHouses(Array.isArray(ghData) ? ghData : []);
      } catch {
        setStates([]);
        setGuestHouses([]);
      }
    };

    const fetchBooking = async () => {
      try {
        setLoading(true);
        setError("");
        const q = new URLSearchParams({
          moduleId: String(moduleId || ""),
          userId: String(userId || ""),
          deptId: String(deptId || "")
        });

        const res = await fetch(`${GH_API}/booking/edit/${encodeURIComponent(bookingId)}?${q.toString()}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to load booking details");

        setFormData((prev) => ({
          ...prev,
          VisitorName: data.VisitorName || "",
          VisitorMobile: data.Mobile || "",
          VisitorEMail: data.Email || "",
          Department: data.Department || "",
          Address: data.Address || "",
          StateID: data.StateID ? String(data.StateID) : "",
          CityID: data.CityID ? String(data.CityID) : "",
          RelationWithApplicant: data.Relationship || "",
          GuestHouseID: data.GuestHouseID ? String(data.GuestHouseID) : "",
          RoomType: data.RoomType || "",
          VisitType: data.TypeOfVisit || "",
          NoOfRooms: data.NoOfRooms || "",
          PurposeOfVisit: data.PurposeOfVisit || "",
          FromDate: formatDateInput(data.FromDate),
          ToDate: formatDateInput(data.ToDate),
          NoOfDays: data.NoOfDays || "",
          RecieptNo: data.ReceiptNo || "",
          ReceiptDate: formatDateInput(data.ReceiptDate),
          PaymentMode: data.PaymentMode || "",
          Amount: data.Amount || "",
          NAmount: data.NetAmount || "",
          UPIID: data.UPIID || "",
          Remarks: data.Remarks || "",
          BookingID: data.BookingID || bookingId,
          Status: data.Status || "",
          visitors: Array.isArray(data.visitors) && data.visitors.length
            ? data.visitors.map((v) => ({
                name: v.name || "",
                mobile: v.mobile || "",
                relationship: v.relationship || "Guest"
              })).slice(0, 2)
            : [{ name: data.VisitorName || "", mobile: data.Mobile || "", relationship: data.Relationship || "Self" }]
        }));
      } catch (e) {
        setError(e.message || "Failed to load booking details");
      } finally {
        setLoading(false);
      }
    };

    fetchLists();
    fetchBooking();
  }, [bookingId, deptId, moduleId, userId]);

  useEffect(() => {
    if (!formData.StateID) {
      setCities([]);
      return;
    }

    const loadCities = async () => {
      try {
        const res = await fetch(`${API_BASE}/cities?stateId=${encodeURIComponent(formData.StateID)}`);
        const data = await res.json();
        setCities(Array.isArray(data) ? data : []);
      } catch {
        setCities([]);
      }
    };

    loadCities();
  }, [formData.StateID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "NoOfRooms") {
      const nextCount = Math.min(2, Math.max(1, parseInt(value, 10) || 1));
      setFormData((prev) => {
        const nextVisitors = [...(prev.visitors || [])];
        while (nextVisitors.length < nextCount) {
          nextVisitors.push({ name: "", mobile: "", relationship: "Guest" });
        }
        return { ...prev, NoOfRooms: String(nextCount), visitors: nextVisitors.slice(0, nextCount) };
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVisitorChange = (index, field, value) => {
    setFormData((prev) => {
      const nextVisitors = [...(prev.visitors || [])];
      if (!nextVisitors[index]) return prev;
      nextVisitors[index] = { ...nextVisitors[index], [field]: value };
      return {
        ...prev,
        visitors: nextVisitors,
        VisitorName: index === 0 && field === "name" ? value : prev.VisitorName,
        VisitorMobile: index === 0 && field === "mobile" ? value : prev.VisitorMobile,
        RelationWithApplicant: index === 0 && field === "relationship" ? value : prev.RelationWithApplicant
      };
    });
  };


  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFormData((prev) => ({ ...prev, [name]: files?.[0] || null }));
  };

  const canModify = ["Pending", "Booked"].includes(String(formData.Status || "").trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!canModify) {
      setError("Booking can only be changed when status is Pending or Booked.");
      return;
    }

    const visitors = (formData.visitors || [])
      .map((v) => ({
        name: String(v.name || "").trim(),
        mobile: String(v.mobile || "").replace(/\D/g, ""),
        relationship: String(v.relationship || "").trim() || "Guest"
      }))
      .filter((v) => v.name || v.mobile || v.relationship)
      .slice(0, 2);

    if (!visitors.length || !visitors[0].name || !/^\d{10}$/.test(visitors[0].mobile)) {
      setError("Valid Visitor Name and 10-digit Visitor Mobile are required.");
      return;
    }

    if (visitors.length > 1 && (!visitors[1].name || !/^\d{10}$/.test(visitors[1].mobile))) {
      setError("Second visitor must have valid name and 10-digit mobile.");
      return;
    }

    try {
      setSaving(true);
      const payload = new FormData();
      payload.append("moduleId", String(moduleId || ""));
      payload.append("userId", String(userId || ""));
      payload.append("deptId", String(deptId || ""));
      payload.append("VisitorName", visitors[0].name || "");
      payload.append("VisitorMobile", String(visitors[0].mobile || "").replace(/^0+/, ""));
      payload.append("VisitorEMail", formData.VisitorEMail || "");
      payload.append("Department", formData.Department || "");
      payload.append("Address", formData.Address || "");
      payload.append("CityID", formData.CityID || "");
      payload.append("StateID", formData.StateID || "");
      payload.append("NoOfRooms", String(Math.min(2, Math.max(1, parseInt(formData.NoOfRooms, 10) || visitors.length || 1))));
      payload.append("RelationWithApplicant", visitors[0].relationship || "Self");
      payload.append("Remarks", formData.Remarks || "");
      payload.append("cancelBooking", String(Boolean(formData.cancelBooking)));
      payload.append("visitors", JSON.stringify(visitors.map((v, idx) => ({
        name: v.name,
        mobile: v.mobile,
        relationship: idx === 0 ? (v.relationship || "Self") : (v.relationship || "Guest")
      }))));

      if (formData.SupportDoc) payload.append("SupportDoc", formData.SupportDoc);
      if (formData.ICardOccupant) payload.append("ICardOccupant", formData.ICardOccupant);

      const res = await fetch(`${GH_API}/booking/edit/${encodeURIComponent(bookingId)}`, {
        method: "POST",
        body: payload
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update booking request");

      window.alert(data.message || "Booking Request is successfully updated.");
      if (window.opener) {
        window.close();
      } else {
        navigate("/guesthouse/booking-status");
      }
    } catch (e2) {
      setError(e2.message || "Failed to update booking request");
      window.alert(e2.message || "Failed to update booking request");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ textAlign: "left", marginTop: "10px" }}>
      <h3 className="ctitle">Change Booking Request (By Emp/Department)</h3>

      {error && <div className="alert alert-danger">{error}</div>}
      {!canModify && (
        <div className="alert alert-warning py-1" style={{ fontSize: "13px" }}>
          Booking can only be changed when status is Pending or Booked.
        </div>
      )}

      <div className="col-lg-10">
        <form
          onSubmit={handleSubmit}
          encType="multipart/form-data"
          name="OffLineBookingForm"
          id="OffLineBookingForm"
          className="guesthouse-booking-form"
        >
          {/* Visitor 1 details */}
          <div className="change-request-row-contact">
            <div className="booking-control-item">
              <label className="form-label">Name of Visitor</label>
              <input
                className="form-control form-control-sm"
                name="VisitorName"
                type="text"
                value={formData.visitors?.[0]?.name || ""}
                onChange={(e) => handleVisitorChange(0, "name", e.target.value)}
                required
                disabled={!canModify || formData.cancelBooking}
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Mobile</label>
              <input
                className="form-control form-control-sm"
                name="VisitorMobile"
                type="text"
                value={formData.visitors?.[0]?.mobile || ""}
                onChange={(e) => handleVisitorChange(0, "mobile", e.target.value)}
                required
                disabled={!canModify || formData.cancelBooking}
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Department</label>
              <input
                className="form-control form-control-sm"
                name="Department"
                type="text"
                value={formData.Department}
                onChange={handleInputChange}
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">EMail</label>
              <input
                className="form-control form-control-sm"
                name="VisitorEMail"
                type="text"
                value={formData.VisitorEMail}
                onChange={handleInputChange}
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Relationship</label>
              <input
                className="form-control form-control-sm"
                name="RelationWithApplicant"
                type="text"
                value={formData.visitors?.[0]?.relationship || ""}
                onChange={(e) => handleVisitorChange(0, "relationship", e.target.value)}
                disabled={!canModify || formData.cancelBooking}
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Purpose of Visit</label>
              <input className="form-control form-control-sm" value={formData.PurposeOfVisit} readOnly />
            </div>
          </div>

          {/* Address row */}
          <div className="visitor-location-row">
            <div className="visitor-location-item visitor-location-address">
              <label className="form-label">Permanent Address</label>
              <input
                className="form-control form-control-sm"
                name="Address"
                type="text"
                value={formData.Address}
                onChange={handleInputChange}
              />
            </div>
            <div className="visitor-location-item visitor-location-state">
              <label className="form-label">State</label>
              <select
                className="form-select form-select-sm"
                name="StateID"
                value={formData.StateID}
                onChange={handleInputChange}
              >
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s.StateID} value={s.StateID}>{s.State}</option>
                ))}
              </select>
            </div>
            <div className="visitor-location-item visitor-location-city">
              <label className="form-label">City</label>
              <select
                className="form-select form-select-sm"
                name="CityID"
                value={formData.CityID}
                onChange={handleInputChange}
              >
                <option value="">Select City</option>
                {cities.map((c) => (
                  <option key={c.CityID} value={c.CityID}>{c.City}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Guest house / booking details row */}
          <div className="change-request-row-booking">
            <div className="booking-control-item booking-control-guesthouse">
              <label className="form-label">Select Guest House</label>
              <select
                className="form-select form-select-sm"
                name="GuestHouseID"
                value={formData.GuestHouseID}
                disabled
              >
                <option value="">Select Guest House</option>
                {guestHouses.map((g) => (
                  <option key={g.GuestHouseID} value={g.GuestHouseID}>{g.GuestHouse}</option>
                ))}
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Room Type</label>
              <select className="form-select form-select-sm" name="RoomType" value={formData.RoomType} disabled>
                <option value="R">Room</option>
                <option value="S">Suite</option>
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Type of Visit</label>
              <select className="form-select form-select-sm" name="VisitType" value={formData.VisitType} disabled>
                <option value="O">Official</option>
                <option value="P">Personal</option>
              </select>
            </div>

            <div className="booking-control-item booking-control-rooms">
              <label className="form-label">NoOfRooms</label>
              <input
                className="form-control form-control-sm"
                name="NoOfRooms"
                type="number"
                value={formData.NoOfRooms}
                disabled
              />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Check-In Date</label>
              <input className="form-control form-control-sm" type="date" value={formData.FromDate} disabled />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Check-Out Date</label>
              <input className="form-control form-control-sm" type="date" value={formData.ToDate} disabled />
            </div>

            <div className="booking-control-item booking-control-days">
              <label className="form-label">No. Of Days</label>
              <input className="form-control form-control-sm" type="number" value={formData.NoOfDays} disabled />
            </div>
          </div>


          {/* Payment row */}
          <div className="change-request-row-payment">
            <div className="booking-control-item">
              <label className="form-label">Invoice No.</label>
              <input className="form-control form-control-sm" value={formData.RecieptNo} readOnly />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Receipt Date</label>
              <input className="form-control form-control-sm" type="date" name="ReceiptDate" value={formData.ReceiptDate} disabled />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Payment Mode</label>
              <select className="form-select form-select-sm" name="PaymentMode" value={formData.PaymentMode} disabled>
                <option value="P">Pending</option>
                <option value="C">Cash</option>
                <option value="Q">Cheque</option>
                <option value="O">Online</option>
                <option value="F">Free Charge</option>
              </select>
            </div>

            <div className="booking-control-item">
              <label className="form-label">Amount</label>
              <input className="form-control form-control-sm" value={formData.Amount} readOnly />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Net Amount</label>
              <input className="form-control form-control-sm" value={formData.NAmount} readOnly />
            </div>

            <div className="booking-control-item">
              <label className="form-label">Transaction ID</label>
              <input className="form-control form-control-sm" value={formData.UPIID} disabled />
            </div>
          </div>

          {/* Files + Remarks + Actions row */}
          <div className="booking-edit-extra-row">
            <div className="visitor-extra-item">
              <label className="form-label">Official Letter (150KB)</label>
              <input
                type="file"
                name="SupportDoc"
                className="form-control form-control-sm"
                accept=".pdf,.png,.jpg,.jpeg,.tif,.bmp"
                onChange={handleFileChange}
              />
            </div>

            <div className="visitor-extra-item">
              <label className="form-label">ID Card Occupant (150KB)</label>
              <input
                type="file"
                name="ICardOccupant"
                className="form-control form-control-sm"
                accept=".pdf,.png,.jpg,.jpeg,.tif,.bmp"
                onChange={handleFileChange}
              />
            </div>

            <div className="visitor-extra-item">
              <label className="form-label">Remarks</label>
              <input className="form-control form-control-sm" value={formData.Remarks} readOnly />
            </div>

            <div className="visitor-extra-item">
              <label className="form-label">Booking ID</label>
              <input className="form-control form-control-sm" value={formData.BookingID} readOnly />
            </div>
          </div>

          <div className="col-sm-12" style={{ marginTop: "0.5rem" }}>
            <label className="me-3" style={{ fontWeight: 500 }}>
              <input
                type="checkbox"
                className="form-check-input me-1"
                checked={Boolean(formData.cancelBooking)}
                onChange={(e) => setFormData((prev) => ({ ...prev, cancelBooking: e.target.checked }))}
                disabled={!canModify}
              />
              Cancel full booking
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || loading || !canModify}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
